'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { useLocale } from 'next-intl';
// Static imports for all locale card backgrounds — bundled, available offline
import cardsBgsPl from '@/public/assets/card_bgs/cards_bgs_pl.json';
import cardsBgsEn from '@/public/assets/card_bgs/cards_bgs_en.json';
import cardsBgsDe from '@/public/assets/card_bgs/cards_bgs_de.json';

const CARDS_BGS_BY_LOCALE = { pl: cardsBgsPl, en: cardsBgsEn, de: cardsBgsDe };

const CardContext = createContext(null);

/**
 * ZAMIANA WŁAŚCIWOŚCI KART PRZEZ TRYB — USUNIĘTA ŚWIADOMIE.
 *
 * Wcześniej w trybie Bez Wytchnienia część kart podmieniała `effects`/`rewards`/
 * `rewardText` (i `synergy.paths[].effects`) na warianty `relentless*`. Przez to ta
 * sama karta wyglądała inaczej w rozgrywce niż na `cards-list` — a w planszówce,
 * czyli docelowym medium, jeden kartonik nie może mieć dwóch nadruków.
 *
 * Funkcja zostaje jako jedno miejsce, w którym można by tę decyzję cofnąć, ale dziś
 * zwraca kartę bez zmian. Lustro backendowego `GameModeRules.getEffective*`.
 * Dane wariantów zdjęto z bazy (`backend/scripts/usun-zamiane-trybu.js`).
 */
function withGameMode(card) {
  return card;
}

/**
 * Card Provider - Loads and provides access to all card data
 */
export function CardProvider({ children }) {
  const locale = useLocale();
  const [cards, setCards] = useState({});
  const [cardsByCategory, setCardsByCategory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      setIsLoading(true);
      // Use static import — works offline without fetch
      const cardsData = CARDS_BGS_BY_LOCALE[locale] || CARDS_BGS_BY_LOCALE['pl'];
      const categories = cardsData.cards;
      const cardsMap = {};

      Object.entries(categories).forEach(([category, categoryCards]) => {
        categoryCards.forEach(card => {
          cardsMap[card.id] = card;
        });
      });
      setCards(cardsMap);
      setCardsByCategory(categories);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading cards:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [locale]);

  // Sentinel dla kart zakrytych przez Cienie Zaułków
  const SHADOW_CARD = {
    id: '__SHADOW__',
    isShadow: true,
    name: 'Zakryta karta',
    type: 'shadow',
    description: 'Karta zakryta przez Cienie Zaułków',
    imageSrc: '/assets/cards/rewers/rewers_karta_targowiska.webp',
    effects: []
  };

  /**
   * Get full card data by ID
   * @param {string} cardId - Card ID (e.g., "ally_handlarz")
   * @returns {Object|null} Full card data or null
   */
  const getCard = (cardId) => {
    if (cardId === '__SHADOW__') return SHADOW_CARD;
    return withGameMode(cards[cardId]) || null;
  };

  /**
   * Get multiple cards by IDs
   * @param {string[]} cardIds - Array of card IDs
   * @returns {Object[]} Array of card data objects
   */
  const getCards = (cardIds) => {
    if (!cardIds || !Array.isArray(cardIds)) return [];
    return cardIds.map(id => {
      if (!id) return null; // Pusty slot (karta zakupiona, slot czeka na uzupełnienie)
      if (id === '__SHADOW__') return SHADOW_CARD;
      if (cards[id]) return withGameMode(cards[id]);
      // Fallback dla nieznanych kart (np. starsze ID negatywnych kart)
      // Nie ukrywaj ich - pokaż placeholder żeby ręka miała poprawną liczbę kart
      console.warn(`[CardContext] Nieznane ID karty: "${id}"`);
      return {
        id,
        name: id === 'wound' ? 'Karta Rany' : id === 'shame' ? 'Karta Wstydu' : id === 'curse' ? 'Klątwa' : `Nieznana karta (${id})`,
        type: 'negative',
        description: 'Karta negatywna',
        imageSrc: '/assets/card_bgs/negative/Karta Rany.webp',
        effects: []
      };
    });
  };

  /**
   * Get cards by category
   * @param {string} category - Category name (e.g., "allies", "monsters")
   * @returns {Object[]} Array of cards in category
   */
  const getCardsByCategory = (category) => {
    return cardsByCategory[category] || [];
  };

  return (
    <CardContext.Provider value={{
      cards,
      cardsByCategory,
      isLoading,
      error,
      getCard,
      getCards,
      getCardsByCategory,
    }}>
      {children}
    </CardContext.Provider>
  );
}

/**
 * Hook to access card data
 */
export function useCards() {
  const context = useContext(CardContext);
  if (!context) {
    throw new Error('useCards must be used within a CardProvider');
  }
  return context;
}

export default CardContext;
